import { Button } from '@unified-lk/ui'
import { formatDateTime, formatStatusLabel } from '../../lib/format'
import type { StaffCommunicationsData } from '../../types/plans'

export function StaffCommunicationsSection({
  data,
  loading,
  error,
  onRetry,
}: {
  data: StaffCommunicationsData | null
  loading: boolean
  error: string | null
  onRetry: () => void
}) {
  if (loading) {
    return <div className="rounded-[28px] border border-slate-200/70 bg-white p-6 text-sm leading-6 text-slate-500 shadow-[0_18px_42px_rgba(15,23,42,0.08)]"><p>Загружаем communications...</p></div>
  }

  if (!data) {
    return (
      <div className="grid gap-3 rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <p className="text-sm leading-6 text-slate-500">{error ?? 'Коммуникации пока недоступны.'}</p>
        <Button variant="secondary" type="button" onClick={onRetry}>Повторить</Button>
      </div>
    )
  }

  return (
    <div className="grid gap-4">
      <section className="grid gap-4 rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Shared / Communications</p>
            <h2 className="text-xl font-semibold text-slate-900">Очередь коммуникаций</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">Единая staff-витрина каналов связи, ожидающих ответов и patient follow-up.</p>
          </div>
          <Button variant="secondary" type="button" onClick={onRetry}>Обновить</Button>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-white px-4 py-3 shadow-sm"><span className="text-[13px] text-slate-500">Непрочитанные треды</span><strong className="text-slate-900">{data.summary.unread_threads}</strong></div>
          <div className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-white px-4 py-3 shadow-sm"><span className="text-[13px] text-slate-500">Ждут ответа</span><strong className="text-slate-900">{data.summary.patients_waiting_reply}</strong></div>
          <div className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-white px-4 py-3 shadow-sm"><span className="text-[13px] text-slate-500">WhatsApp приоритет</span><strong className="text-slate-900">{data.summary.preferred_whatsapp}</strong></div>
          <div className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-white px-4 py-3 shadow-sm"><span className="text-[13px] text-slate-500">Нужен follow-up</span><strong className="text-slate-900">{data.summary.manual_follow_up}</strong></div>
        </div>

        <div className="flex flex-wrap gap-2.5">
          {data.channel_overview.map((channel) => (
            <span key={channel.channel} className="rounded-full border border-sky-100 bg-sky-50 px-4 py-2.5 text-sm font-medium text-sky-800">
              {channel.label}: {channel.count}
            </span>
          ))}
        </div>
      </section>

      <section className="grid gap-4 rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.threads.map((thread) => (
            <article key={thread.id} className="grid gap-3 rounded-[24px] border border-slate-200/70 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <strong className="text-slate-900">{thread.patient_name}</strong>
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">{formatStatusLabel(thread.status)}</span>
              </div>
              <p className="text-sm leading-6 text-slate-500">{thread.subject}</p>
              <div className="flex flex-wrap gap-2 text-sm text-slate-500">
                <span>{thread.channel} · {thread.preferred_channel}</span>
                <span>Unread: {thread.unread_count}</span>
              </div>
              <div className="flex flex-wrap gap-2 text-sm text-slate-500">
                <span>{thread.last_message}</span>
              </div>
              <div className="flex flex-wrap gap-2 text-sm text-slate-500">
                <span>{formatDateTime(thread.last_message_at)}</span>
                <span>{thread.owner_name}</span>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}
